import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { MapPin, Menu, X, LogOut, User } from 'lucide-react';

const Header = () => {
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Navigation items based on authentication status
  const publicNavigation = [
    { name: 'Doctors', href: '/doctors' },
    { name: 'Pharmacy', href: '/pharmacy' },
    { name: 'Lab', href: '/lab' },
    { name: 'Nutrition', href: '/nutrition' },
    { name: 'Insurance', href: '/insurance' },
    { name: 'Blog', href: '/blogs' },
    { name: 'Privacy', href: '/privacy', highlight: true }
  ];

  const privateNavigation = [
    { name: 'Doctors', href: '/doctors' },
    { name: 'Pharmacy', href: '/pharmacy' },
    { name: 'Lab', href: '/lab' },
    { name: 'Nutrition', href: '/nutrition' },
    { name: 'Insurance', href: '/insurance' },
    { name: 'Blog', href: '/blogs' },
    { name: 'Privacy', href: '/privacy', highlight: true },
    { name: 'Vitals', href: '/vitals' },
    { name: 'Account', href: '/account' }
  ];

  const navigation = isAuthenticated ? privateNavigation : publicNavigation;

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo - Centered on mobile, left on desktop */}
          <div className="flex items-center justify-center flex-1 md:flex-none">
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src="https://customer-assets.emergentagent.com/job_healthcare-hub-25/artifacts/e5l587sq_GEETH365.jpg" 
                alt="GEETH365 Logo" 
                className="w-10 h-10 rounded-full border-2 border-orange-200 group-hover:border-orange-400 transition-colors object-cover"
              />
              <div className="flex flex-col">
                <span className="text-xl font-bold text-gray-900 group-hover:text-orange-600 transition-colors">
                  GEETH365
                </span>
                <span className="text-xs text-orange-600 font-medium">Healthcare</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.href)
                    ? 'bg-orange-100 text-orange-700'
                    : item.highlight
                    ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200'
                    : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Location & Auth Buttons */}
          <div className="hidden md:flex items-center space-x-3">
            <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 hover:bg-orange-50">
              <MapPin className="w-4 h-4 mr-1" />
              530041
            </Button>
            
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>Hi, {user?.name}</span>
                </div>
                <Button 
                  onClick={logout}
                  variant="outline" 
                  size="sm" 
                  className="text-gray-600 border-gray-200 hover:bg-gray-50"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  Logout
                </Button>
              </div>
            ) : (
              <Link to="/auth">
                <Button size="sm" className="bg-orange-600 hover:bg-orange-700">
                  Sign Up Free
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600"
            >
              {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t bg-white py-4">
            <div className="space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive(item.href)
                      ? 'bg-orange-100 text-orange-700'
                      : item.highlight
                      ? 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-blue-200'
                      : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-3 space-y-2">
                <Button variant="outline" className="w-full text-orange-600 border-orange-200 hover:bg-orange-50">
                  <MapPin className="w-4 h-4 mr-1" />
                  Change Location
                </Button>
                
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <div className="text-center text-sm text-gray-600 py-2">
                      Hi, {user?.name}
                    </div>
                    <Button 
                      onClick={logout}
                      variant="outline" 
                      className="w-full text-gray-600 border-gray-200"
                    >
                      <LogOut className="w-4 h-4 mr-1" />
                      Logout
                    </Button>
                  </div>
                ) : (
                  <Link to="/auth" className="block">
                    <Button className="w-full bg-orange-600 hover:bg-orange-700">
                      Sign Up Free
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* WhatsApp Support Strip */}
      <div className="bg-orange-600 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <a 
            href="https://wa.me/917337541113?text=Hello GEETH365, I need help with healthcare services"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium hover:text-orange-200 transition-colors"
          >
            📱 Need Help? Chat on WhatsApp - 7337541113
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;